import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import styled from 'styled-components';
import posthog from 'posthog-js';
import { utils } from '@makeship/core';
import { Caption, H3, H8 } from '../Typography';
import { useStore } from '../../store';
import { isUserLoggedIn } from '../../utils/analytics';
import { parseFeaturedCollectionMetafields, renderDefaultCreatorLogoText } from '../../utils/collections';
import { parseProductMetafieldsFromCollection } from '../../utils/product';

const GridContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 16px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding: 0 64px;
  }
`;

const StyledImage = styled(Image)`
  width: 100%;
  object-fit: contain;
  border-radius: 175px;
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 12px 10px 12px 0;
  min-width: 112px;
  flex: 0 0 auto;

  &:not(:first-child) {
    margin-left: 10px;
  }
`;

const CreatorImageWrapper = styled.div`
  margin-bottom: 12px;
  height: 112px;
  width: 112px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    height: 175px;
    width: 175px;
  }
`;

const CreatorTitle = styled(H8)`
  color: ${({ theme }) => theme.colors.neutral7};
  font-size: 18px;
  max-width: 112px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    font-size: 22px;
    max-width: 175px;
  }
`;

const Hyperlink = styled.a``;

const DefaultCreatorLogo = styled.div`
  display: flex;
  border: ${({ theme }) => `1px solid ${theme.colors.defaultCreatorLogoBorder}`};
  background: ${({ theme }) => theme.colors.defaultCreatorLogoBackground};
  object-fit: contain;
  border-radius: 175px;
  height: 112px;
  width: 112px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    height: 175px;
    width: 175px;
  }
`;

const DefaultCreatorLogoText = styled(H3)`
  font-size: 40px;
  margin: auto;
  width: 100%;
`;

const CreatorCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 175px;
`;

type CreatorGridProps = {
  creatorCollections: CollectionEdgeResponse[];
  sectionName: string;
  isComingSoon?: boolean;
};

const CreatorGrid: React.FC<CreatorGridProps> = ({
  creatorCollections,
  sectionName,
  isComingSoon,
}: CreatorGridProps) => {
  const { state } = useStore();

  const renderCarouselItems = () =>
    creatorCollections.map((collection) => {
      const { node } = collection;
      const { metafields } = node;
      const { creatorLogoURL, creatorAvatarURL } = parseFeaturedCollectionMetafields(metafields.edges);
      const creatorLogoSrc = creatorLogoURL || creatorAvatarURL;
      const productMetafields = node?.products?.[0]?.node?.metafields?.edges || [];
      const { launch } = parseProductMetafieldsFromCollection(productMetafields);
      const launchDate = utils.getDateLocalCustomFormat(launch || '', 'MMMM D');

      return (
        <ItemWrapper key={node?.id}>
          <Link href={`/shop/creator/${node?.handle}`} passHref>
            <Hyperlink
              onClick={() => {
                posthog.capture(`featuredCreator_carousel_click`, {
                  creator_handle: node?.handle,
                  creator_title: node?.title,
                  is_logged_in: isUserLoggedIn(state.user),
                  section_name: sectionName,
                });
              }}
            >
              <CreatorCardWrapper>
                <CreatorImageWrapper>
                  {creatorLogoSrc ? (
                    <StyledImage src={creatorLogoSrc} alt={node?.title} width={175} height={175} priority />
                  ) : (
                    <DefaultCreatorLogo>
                      <DefaultCreatorLogoText>{renderDefaultCreatorLogoText(node?.title)}</DefaultCreatorLogoText>
                    </DefaultCreatorLogo>
                  )}
                </CreatorImageWrapper>
                <CreatorTitle>{node?.title || ''}</CreatorTitle>
                {isComingSoon && !!launch && <Caption>🗓️ {launchDate}</Caption>}
              </CreatorCardWrapper>
            </Hyperlink>
          </Link>
        </ItemWrapper>
      );
    });

  return <GridContainer>{renderCarouselItems()}</GridContainer>;
};

export default CreatorGrid;
